<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import axios from 'axios';
import axiosInstance from '@/services/interceptor.services';
import { VITE_API_URL } from '@/utils/runtimeConfig';
import { TranslationService } from '@/services/translation.services';
import { token } from '@/refs/common.refs';
import { organizationIdCommerce } from '@/refs/commerce.refs';

const { t } = useI18n({ useScope: 'global' });

const router = useRouter();
const route = useRoute();
const storeId = ref((route.query.storeId as string) || '');

/* ── Traduction (backend interne) ────────────────────────────────────────── */
async function translateAll(
  src: 'fr' | 'en' | 'nl',
  get: () => string,
  set: (l: 'fr' | 'en' | 'nl', v: string) => void,
  busy: { value: boolean },
) {
  const text = get();
  if (!text.trim() || busy.value) return;
  busy.value = true;
  try {
    const translated = await TranslationService.translateText(text, src);
    (['fr', 'en', 'nl'] as const)
      .filter((l) => l !== src)
      .forEach((l) => set(l, translated[l]));
  } finally {
    busy.value = false;
  }
}

/* ── Catégories ─────────────────────────────────────────────────────────── */
type Category = {
  id: string;
  designation: any;
  description?: any;
  modules?: string[];
};
const categories = ref<Category[]>([]);
const loadingCats = ref(false);

function catLabel(c: Category): string {
  const d = c.designation;
  if (!d) return '—';
  if (typeof d === 'string') return d;
  return d.fr || d.en || d.nl || '—';
}

async function fetchCategories() {
  const orgId = organizationIdCommerce.value;
  if (!orgId) return;
  loadingCats.value = true;
  try {
    const r = await axiosInstance.get(
      `products/categories/search?pagination=false&organizationIds=${orgId}`,
    );
    const d = r.data as any;
    categories.value = (d?.data ?? d ?? []).filter(Boolean);
  } catch {
    /* ignore */
  } finally {
    loadingCats.value = false;
  }
}
watch(
  organizationIdCommerce,
  (v) => {
    if (v) fetchCategories();
  },
  { immediate: true },
);

const selectedCats = ref<string[]>([]);
function toggleCat(id: string) {
  const i = selectedCats.value.indexOf(id);
  if (i >= 0) selectedCats.value.splice(i, 1);
  else selectedCats.value.push(id);
}

/* Formulaire inline création / édition de catégorie */
const showCatForm = ref(false);
const editCatId = ref<string | null>(null);
const catFormLang = ref<'fr' | 'en' | 'nl'>('fr');
const catNameFr = ref('');
const catNameEn = ref('');
const catNameNl = ref('');
const catDescFr = ref('');
const catDescEn = ref('');
const catDescNl = ref('');
const catModules = ref<string[]>([]);
const catSaving = ref(false);
const catError = ref<string | null>(null);
const transNameCat = ref(false);
const transDescCat = ref(false);
const confirmDelCat = ref<Category | null>(null);
const deletingCat = ref(false);

const CATEGORY_MODULE_OPTIONS = [
  {
    id: 'click_and_collect',
    icon: 'pi-shopping-cart',
    labelKey: 'commerceDetail.modulesCnc',
  },
  {
    id: 'pos_register',
    icon: 'pi-calculator',
    labelKey: 'commerceDetail.modulesCaisse',
  },
  {
    id: 'pds_display',
    icon: 'pi-desktop',
    labelKey: 'commerceDetail.modulesPds',
  },
  { id: 'kiosk', icon: 'pi-tablet', labelKey: 'commerceDetail.modulesKiosk' },
] as const;
function toggleCatModule(id: string) {
  const idx = catModules.value.indexOf(id);
  if (idx >= 0) catModules.value.splice(idx, 1);
  else catModules.value.push(id);
}

function openNewCat() {
  editCatId.value = null;
  catFormLang.value = 'fr';
  catNameFr.value = catNameEn.value = catNameNl.value = '';
  catDescFr.value = catDescEn.value = catDescNl.value = '';
  catModules.value = [];
  catError.value = null;
  showCatForm.value = true;
}

// Pré-remplissage auto des langues manquantes à partir de la première langue
// non vide trouvée, sans attendre un blur de l'utilisateur.
async function fillMissingCatTranslations(
  values: Record<'fr' | 'en' | 'nl', string>,
  setters: Record<'fr' | 'en' | 'nl', (v: string) => void>,
  busy: { value: boolean },
  stillCurrent: () => boolean,
) {
  const source = (['fr', 'en', 'nl'] as const).find((l) => values[l].trim());
  if (!source) return;
  const missing = (['fr', 'en', 'nl'] as const).filter(
    (l) => l !== source && !values[l].trim(),
  );
  if (!missing.length) return;
  busy.value = true;
  try {
    const translated = await TranslationService.translateText(
      values[source],
      source,
    );
    if (!stillCurrent()) return;
    missing.forEach((l) => setters[l](translated[l]));
  } finally {
    if (stillCurrent()) busy.value = false;
  }
}

async function openEditCat(c: Category) {
  editCatId.value = c.id;
  const d = c.designation;
  catNameFr.value = typeof d === 'string' ? d : (d?.fr ?? '');
  catNameEn.value = typeof d === 'string' ? '' : (d?.en ?? '');
  catNameNl.value = typeof d === 'string' ? '' : (d?.nl ?? '');
  const desc = c.description;
  catDescFr.value = typeof desc === 'string' ? desc : (desc?.fr ?? '');
  catDescEn.value = typeof desc === 'string' ? '' : (desc?.en ?? '');
  catDescNl.value = typeof desc === 'string' ? '' : (desc?.nl ?? '');
  catModules.value = Array.isArray(c.modules) ? [...c.modules] : [];
  catFormLang.value = 'fr';
  catError.value = null;
  showCatForm.value = true;

  const thisCatId = c.id;
  const stillCurrent = () => editCatId.value === thisCatId;
  await fillMissingCatTranslations(
    { fr: catNameFr.value, en: catNameEn.value, nl: catNameNl.value },
    {
      fr: (v) => (catNameFr.value = v),
      en: (v) => (catNameEn.value = v),
      nl: (v) => (catNameNl.value = v),
    },
    transNameCat,
    stillCurrent,
  );
  await fillMissingCatTranslations(
    { fr: catDescFr.value, en: catDescEn.value, nl: catDescNl.value },
    {
      fr: (v) => (catDescFr.value = v),
      en: (v) => (catDescEn.value = v),
      nl: (v) => (catDescNl.value = v),
    },
    transDescCat,
    stillCurrent,
  );
}
function closeCatForm() {
  showCatForm.value = false;
  editCatId.value = null;
}

function onCatNameBlur(lang: 'fr' | 'en' | 'nl') {
  if (catFormLang.value !== lang) return;
  const getters: Record<string, () => string> = {
    fr: () => catNameFr.value,
    en: () => catNameEn.value,
    nl: () => catNameNl.value,
  };
  translateAll(
    lang,
    getters[lang],
    (l, v) => {
      if (l === 'fr') catNameFr.value = v;
      if (l === 'en') catNameEn.value = v;
      if (l === 'nl') catNameNl.value = v;
    },
    transNameCat,
  );
}
function onCatDescBlur(lang: 'fr' | 'en' | 'nl') {
  if (catFormLang.value !== lang) return;
  const getters: Record<string, () => string> = {
    fr: () => catDescFr.value,
    en: () => catDescEn.value,
    nl: () => catDescNl.value,
  };
  translateAll(
    lang,
    getters[lang],
    (l, v) => {
      if (l === 'fr') catDescFr.value = v;
      if (l === 'en') catDescEn.value = v;
      if (l === 'nl') catDescNl.value = v;
    },
    transDescCat,
  );
}

async function saveCat() {
  if (!catNameFr.value.trim()) {
    catError.value = 'La désignation FR est obligatoire';
    return;
  }
  catSaving.value = true;
  catError.value = null;
  try {
    const payload: any = {
      designation: {
        fr: catNameFr.value.trim(),
        en: catNameEn.value.trim(),
        nl: catNameNl.value.trim(),
      },
      description: {
        fr: catDescFr.value.trim(),
        en: catDescEn.value.trim(),
        nl: catDescNl.value.trim(),
      },
      modules: catModules.value,
    };
    if (editCatId.value) {
      if (organizationIdCommerce.value)
        payload.organizationId = organizationIdCommerce.value;
      await axiosInstance.put(
        `products/categories/update/${editCatId.value}`,
        payload,
      );
    } else {
      if (storeId.value) payload.restaurantId = storeId.value;
      const r = await axiosInstance.post('products/categories/create', payload);
      const created = (r.data as any)?.data ?? (r.data as any);
      if (created?.id) categories.value.push(created);
    }
    await fetchCategories();
    closeCatForm();
  } catch (err: any) {
    const raw = err?.response?.data?.message;
    catError.value = Array.isArray(raw)
      ? raw[0]
      : (raw ?? err?.message ?? 'Erreur');
  } finally {
    catSaving.value = false;
  }
}

async function deleteCat() {
  if (!confirmDelCat.value) return;
  deletingCat.value = true;
  try {
    await axiosInstance.delete(
      `products/categories/delete/${confirmDelCat.value.id}`,
    );
    selectedCats.value = selectedCats.value.filter(
      (id) => id !== confirmDelCat.value!.id,
    );
    await fetchCategories();
  } catch {
    /* ignore */
  } finally {
    deletingCat.value = false;
    confirmDelCat.value = null;
  }
}

/* ── Désignation produit ────────────────────────────────────────────────── */
const nameLang = ref<'fr' | 'en' | 'nl'>('fr');
const nameFr = ref('');
const nameEn = ref('');
const nameNl = ref('');
const transName = ref(false);
function onNameBlur(lang: 'fr' | 'en' | 'nl') {
  if (nameLang.value !== lang) return;
  const getters: Record<string, () => string> = {
    fr: () => nameFr.value,
    en: () => nameEn.value,
    nl: () => nameNl.value,
  };
  translateAll(
    lang,
    getters[lang],
    (l, v) => {
      if (l === 'fr') nameFr.value = v;
      if (l === 'en') nameEn.value = v;
      if (l === 'nl') nameNl.value = v;
    },
    transName,
  );
}

/* ── Description ────────────────────────────────────────────────────────── */
const descLang = ref<'fr' | 'en' | 'nl'>('fr');
const descFr = ref('');
const descEn = ref('');
const descNl = ref('');
const transDesc = ref(false);
function onDescBlur(lang: 'fr' | 'en' | 'nl') {
  if (descLang.value !== lang) return;
  const getters: Record<string, () => string> = {
    fr: () => descFr.value,
    en: () => descEn.value,
    nl: () => descNl.value,
  };
  translateAll(
    lang,
    getters[lang],
    (l, v) => {
      if (l === 'fr') descFr.value = v;
      if (l === 'en') descEn.value = v;
      if (l === 'nl') descNl.value = v;
    },
    transDesc,
  );
}

/* ── Prix & type ────────────────────────────────────────────────────────── */
const PRODUCT_TYPES = computed(() => [
  { value: 'food', label: t('product.typeFood') },
  { value: 'extra', label: t('product.typeExtra') },
  { value: 'food&extra', label: t('product.typeFoodExtra') },
]);

const price = ref('');
const productType = ref('food');
const priceUnit = ref('PER_PIECE');
const preparationTime = ref('');

const PRICE_UNIT_OPTIONS = computed(() => [
  { value: 'PER_PIECE', label: t('product.perPiece') },
  { value: 'PER_KG', label: t('product.perKg') },
  { value: 'PER_100G', label: t('product.per100g') },
  { value: 'PER_LITER', label: t('product.perLiter') },
]);

/* ── TVA ────────────────────────────────────────────────────────────────── */
interface TaxRate {
  id: string;
  name: string;
  rate: number;
  country: string;
}
const taxRates = ref<TaxRate[]>([]);
const loadingTaxRates = ref(false);
const tvaId = ref('');
const tvaDropdownOpen = ref(false);
const tvaSearch = ref('');

async function fetchTaxRates() {
  if (!storeId.value) return;
  loadingTaxRates.value = true;
  try {
    const params = new URLSearchParams({
      pagination: 'false',
      pageSize: '200',
      storeId: storeId.value,
    });
    const r = await axiosInstance.get(`taxes/search?${params}`);
    const d = r.data as any;
    taxRates.value = d?.data ?? [];
  } catch {
    taxRates.value = [];
  } finally {
    loadingTaxRates.value = false;
  }
}
watch(
  storeId,
  (v) => {
    if (v) fetchTaxRates();
  },
  { immediate: true },
);

const filteredTaxRates = computed(() =>
  taxRates.value.filter((tx) =>
    tx.name.toLowerCase().includes(tvaSearch.value.trim().toLowerCase()),
  ),
);
const selectedTaxRate = computed(() =>
  taxRates.value.find((tx) => tx.id === tvaId.value),
);
function selectTvaId(id: string) {
  tvaId.value = id;
  tvaDropdownOpen.value = false;
  tvaSearch.value = '';
}

/* ── Image ──────────────────────────────────────────────────────────────── */
const imgFile = ref<File | null>(null);
const imgPreview = ref<string | null>(null);
const imgInputRef = ref<HTMLInputElement | null>(null);
const imgUploading = ref(false);

function onImgChange(e: Event) {
  const f = (e.target as HTMLInputElement).files?.[0] ?? null;
  if (!f) return;
  imgFile.value = f;
  const reader = new FileReader();
  reader.onload = (ev) => {
    imgPreview.value = ev.target?.result as string;
  };
  reader.readAsDataURL(f);
}
function removeImg() {
  imgFile.value = null;
  imgPreview.value = null;
  if (imgInputRef.value) imgInputRef.value.value = '';
}

async function uploadImage(): Promise<string> {
  if (!imgFile.value) throw new Error('Image requise');
  imgUploading.value = true;
  try {
    const fd = new FormData();
    fd.append('file', imgFile.value);
    fd.append('designation', nameFr.value.trim() || 'product-image');
    if (storeId.value) fd.append('restaurantId', storeId.value);
    const r = await axios.post(`${VITE_API_URL}files/upload`, fd, {
      headers: { Authorization: `Bearer ${token.value?.accessToken}` },
    });
    const d = r.data as any;
    return d?.data?.id ?? d?.id ?? '';
  } finally {
    imgUploading.value = false;
  }
}

/* ── Variantes (labels multilingues) ───────────────────────────────────── */
type Variant = {
  labelFr: string;
  labelEn: string;
  labelNl: string;
  labelLang: 'fr' | 'en' | 'nl';
  price: string;
  status: 'active' | 'inactive' | 'out-of-stock';
  translating: boolean;
};

const variants = ref<Variant[]>([]);

function addVariant() {
  variants.value.push({
    labelFr: '',
    labelEn: '',
    labelNl: '',
    labelLang: 'fr',
    price: '',
    status: 'active',
    translating: false,
  });
}
function removeVariant(i: number) {
  variants.value.splice(i, 1);
}

function onVariantLabelBlur(i: number, lang: 'fr' | 'en' | 'nl') {
  const v = variants.value[i];
  if (v.labelLang !== lang) return;
  const getters: Record<string, () => string> = {
    fr: () => v.labelFr,
    en: () => v.labelEn,
    nl: () => v.labelNl,
  };
  const busy = {
    get value() {
      return v.translating;
    },
    set value(x: boolean) {
      v.translating = x;
    },
  };
  translateAll(
    lang,
    getters[lang],
    (l, val) => {
      if (l === 'fr') v.labelFr = val;
      if (l === 'en') v.labelEn = val;
      if (l === 'nl') v.labelNl = val;
    },
    busy,
  );
}

/* ── AddonGroups ────────────────────────────────────────────────────────── */
type AddonOption = {
  contentFr: string;
  contentEn: string;
  contentNl: string;
  optLang: 'fr' | 'en' | 'nl';
  price: string;
  translating: boolean;
};
type AddonGroup = {
  titleFr: string;
  titleEn: string;
  titleNl: string;
  titleLang: 'fr' | 'en' | 'nl';
  extraType: 'side-dish' | 'supplements' | 'proposal';
  chooseType: 'multiple' | 'unique';
  required: boolean;
  minQty: string;
  maxQty: string;
  options: AddonOption[];
  translating: boolean;
};

const addonGroups = ref<AddonGroup[]>([]);

function addGroup() {
  addonGroups.value.push({
    titleFr: '',
    titleEn: '',
    titleNl: '',
    titleLang: 'fr',
    extraType: 'supplements',
    chooseType: 'multiple',
    required: false,
    minQty: '0',
    maxQty: '1',
    options: [
      {
        contentFr: '',
        contentEn: '',
        contentNl: '',
        optLang: 'fr' as 'fr' | 'en' | 'nl',
        price: '',
        translating: false,
      },
    ],
    translating: false,
  });
}
function removeGroup(i: number) {
  addonGroups.value.splice(i, 1);
}
function addOption(gi: number) {
  addonGroups.value[gi].options.push({
    contentFr: '',
    contentEn: '',
    contentNl: '',
    optLang: 'fr',
    price: '',
    translating: false,
  });
}
function removeOption(gi: number, oi: number) {
  addonGroups.value[gi].options.splice(oi, 1);
}

function onGroupTitleBlur(gi: number, lang: 'fr' | 'en' | 'nl') {
  const g = addonGroups.value[gi];
  if (g.titleLang !== lang) return;
  const getters: Record<string, () => string> = {
    fr: () => g.titleFr,
    en: () => g.titleEn,
    nl: () => g.titleNl,
  };
  const busy = {
    get value() {
      return g.translating;
    },
    set value(x: boolean) {
      g.translating = x;
    },
  };
  translateAll(
    lang,
    getters[lang],
    (l, val) => {
      if (l === 'fr') g.titleFr = val;
      if (l === 'en') g.titleEn = val;
      if (l === 'nl') g.titleNl = val;
    },
    busy,
  );
}

function onOptionBlur(gi: number, oi: number, lang: 'fr' | 'en' | 'nl') {
  const o = addonGroups.value[gi].options[oi];
  if (o.optLang !== lang) return;
  const getters: Record<string, () => string> = {
    fr: () => o.contentFr,
    en: () => o.contentEn,
    nl: () => o.contentNl,
  };
  const busy = {
    get value() {
      return o.translating;
    },
    set value(x: boolean) {
      o.translating = x;
    },
  };
  translateAll(
    lang,
    getters[lang],
    (l, val) => {
      if (l === 'fr') o.contentFr = val;
      if (l === 'en') o.contentEn = val;
      if (l === 'nl') o.contentNl = val;
    },
    busy,
  );
}

/* ── Soumission ─────────────────────────────────────────────────────────── */
const saving = ref(false);
const error = ref<string | null>(null);
const done = ref(false);
const createdId = ref<string | null>(null);

// N'affiche les erreurs de champ (bordure rouge + message) qu'après une
// première tentative de soumission, pas dès l'ouverture du formulaire.
const attemptedSubmit1 = ref(false);
const showNameError = computed(
  () => attemptedSubmit1.value && !nameFr.value.trim(),
);
const showPriceError = computed(
  () =>
    attemptedSubmit1.value && (!price.value || isNaN(parseFloat(price.value))),
);
const showImageError = computed(() => attemptedSubmit1.value && !imgFile.value);

async function submit() {
  attemptedSubmit1.value = true;
  if (!nameFr.value.trim()) {
    error.value = 'La désignation (FR) est obligatoire';
    return;
  }
  if (!price.value || isNaN(parseFloat(price.value))) {
    error.value = 'Le prix est obligatoire';
    return;
  }
  if (!imgFile.value) {
    error.value = 'Une image est requise';
    return;
  }
  if (!organizationIdCommerce.value) {
    error.value = 'Organisation non définie — ouvrir depuis un commerce';
    return;
  }

  saving.value = true;
  error.value = null;
  try {
    /* 1. Upload image */
    const imageId = await uploadImage();

    /* 2. Créer produit */
    const body: any = {
      organizationId: organizationIdCommerce.value,
      designation: {
        fr: nameFr.value.trim(),
        en: nameEn.value.trim(),
        nl: nameNl.value.trim(),
      },
      price: parseFloat(price.value),
      priceUnit: priceUnit.value,
      imageId,
      categoryIds: selectedCats.value,
      type: productType.value,
      needExtra: addonGroups.value.length > 0,
    };
    if (storeId.value) body.restaurantId = storeId.value;
    if (tvaId.value) body.tvaId = tvaId.value;
    if (descFr.value.trim() || descEn.value.trim() || descNl.value.trim()) {
      body.description = {
        fr: descFr.value,
        en: descEn.value,
        nl: descNl.value,
      };
    }

    const prodResp = await axiosInstance.post('products/create', body);
    const prodData = prodResp.data as any;
    const productId: string = prodData?.data?.id ?? prodData?.id ?? '';
    createdId.value = productId;

    if (!productId) throw new Error('ID produit manquant dans la réponse');

    /* 3. Variantes */
    const validVariants = variants.value.filter(
      (v) => v.labelFr.trim() || v.labelEn.trim() || v.labelNl.trim(),
    );
    if (validVariants.length) {
      await axiosInstance.post(
        `products/${productId}/variants/create-variant-bulk`,
        {
          variants: validVariants.map((v) => ({
            label: {
              fr: v.labelFr.trim(),
              en: v.labelEn.trim(),
              nl: v.labelNl.trim(),
            },
            price: parseFloat(v.price) || 0,
            status: v.status,
            optionValueIds: [] as string[],
          })),
        },
      );
    }

    /* 4. AddonGroups */
    for (const g of addonGroups.value) {
      if (!g.titleFr.trim() && !g.titleEn.trim() && !g.titleNl.trim()) continue;
      const validOptions = g.options.filter(
        (o) => o.contentFr.trim() || o.contentEn.trim() || o.contentNl.trim(),
      );
      if (!validOptions.length) continue;
      await axiosInstance.post(`product/${productId}/extras`, {
        title: {
          fr: g.titleFr.trim(),
          en: g.titleEn.trim(),
          nl: g.titleNl.trim(),
        },
        extraType: g.extraType,
        chooseType: g.chooseType,
        required: g.required,
        minQty: parseInt(g.minQty) || 0,
        maxQty: parseInt(g.maxQty) || 1,
        option: validOptions.map((o) => ({
          content: {
            fr: o.contentFr.trim(),
            en: o.contentEn.trim(),
            nl: o.contentNl.trim(),
          },
          type: 'custom' as const,
          price: parseFloat(o.price) || null,
        })),
      });
    }

    done.value = true;
  } catch (err: any) {
    const raw = err?.response?.data?.message;
    error.value = Array.isArray(raw)
      ? raw[0]
      : (raw ?? err?.message ?? 'Une erreur est survenue');
  } finally {
    saving.value = false;
  }
}

const EXTRA_TYPE_LABELS = computed<Record<string, string>>(() => ({
  'side-dish': t('product.typeSideDish'),
  supplements: t('product.typeSupplement'),
  proposal: t('product.typeProposal'),
}));
</script>

<template>
  <div class="cpp" @click="tvaDropdownOpen = false">
    <!-- Breadcrumb -->
    <nav class="cpp__bc">
      <button class="cpp__bc-link" @click="router.back()">
        <i class="pi pi-arrow-left" /> {{ t('product.back') }}
      </button>
    </nav>

    <!-- Succès -->
    <div v-if="done" class="cpp__success">
      <div class="cpp__success-icon"><i class="pi pi-check" /></div>
      <h2 class="cpp__success-title">{{ t('product.productCreated') }}</h2>
      <p class="cpp__success-sub">{{ t('product.productCreatedDesc') }}</p>
      <div class="cpp__success-actions">
        <button class="cpp__btn cpp__btn--ghost" @click="router.back()">
          <i class="pi pi-arrow-left" /> {{ t('product.backToCatalog') }}
        </button>
        <button
          v-if="createdId"
          class="cpp__btn cpp__btn--primary"
          @click="router.push(`/products/${createdId}`)"
        >
          <i class="pi pi-eye" /> {{ t('product.viewProduct') }}
        </button>
      </div>
    </div>

    <template v-else>
      <!-- Header -->
      <div class="cpp__header">
        <div class="cpp__header-icon"><i class="pi pi-box" /></div>
        <div>
          <h1 class="cpp__title">{{ t('product.newProduct') }}</h1>
          <p class="cpp__sub">{{ t('product.newProductSubtitle') }}</p>
        </div>
      </div>

      <div class="cpp__body">
        <!-- ════════════════════════════════════════════════════════════
           1. IMAGE
      ═══════════════════════════════════════════════════════════════ -->
        <section class="cpp__section">
          <div class="cpp__section-hd">
            <div class="cpp__section-ico"><i class="pi pi-image" /></div>
            <div>
              <p class="cpp__section-title">
                Image <span class="cpp__req">*</span>
              </p>
              <p class="cpp__section-sub">{{ t('product.mainPhoto') }}</p>
            </div>
          </div>
          <div class="cpp__img-upload">
            <div
              class="cpp__img-preview-wrap"
              :class="{ 'cpp__input--error': showImageError }"
            >
              <img
                v-if="imgPreview"
                :src="imgPreview"
                class="cpp__img-preview"
                :alt="t('product.mainPhoto')"
              />
              <div v-else class="cpp__img-placeholder">
                <i class="pi pi-camera" />
              </div>
            </div>
            <div class="cpp__img-actions">
              <input
                ref="imgInputRef"
                type="file"
                accept="image/*"
                style="display: none"
                @change="onImgChange"
              />
              <button
                class="cpp__btn cpp__btn--outline"
                @click="imgInputRef?.click()"
              >
                <i class="pi pi-upload" />
                {{
                  imgFile ? t('product.changeImage') : t('product.chooseImage')
                }}
              </button>
              <button
                v-if="imgFile"
                class="cpp__btn cpp__btn--ghost"
                @click="removeImg"
              >
                <i class="pi pi-times" /> {{ t('product.removeImage') }}
              </button>
              <span v-if="imgFile" class="cpp__filename">{{
                imgFile.name
              }}</span>
            </div>
          </div>
          <p v-if="showImageError" class="cpp__field-error">
            {{ t('product.requiredField') }}
          </p>
        </section>

        <div class="cpp__divider" />

        <!-- ════════════════════════════════════════════════════════════
           2. DÉSIGNATION
      ═══════════════════════════════════════════════════════════════ -->
        <section class="cpp__section">
          <div class="cpp__section-hd">
            <div class="cpp__section-ico"><i class="pi pi-tag" /></div>
            <div>
              <p class="cpp__section-title">
                {{ t('product.catDesignation') }}
                <span class="cpp__req">*</span>
              </p>
              <p class="cpp__section-sub">
                {{ t('product.designationSubtitle') }}
              </p>
            </div>
          </div>
          <div class="cpp__fields">
            <div class="cpp__translabel-row">
              <label class="cpp__label">{{ t('product.inputLang') }}</label>
              <div class="cpp__select-wrap" style="max-width: 10rem">
                <select v-model="nameLang" class="cpp__select cpp__select--sm">
                  <option value="fr">Français</option>
                  <option value="en">English</option>
                  <option value="nl">Nederlands</option>
                </select>
                <i class="pi pi-chevron-down cpp__select-icon" />
              </div>
            </div>
            <div class="cpp__field-row cpp__field-row--3">
              <div class="cpp__field">
                <label class="cpp__label"
                  ><span
                    class="cpp__lang-badge"
                    :class="nameLang === 'fr' ? 'cpp__lang-badge--active' : ''"
                    >FR</span
                  >
                  {{ t('product.langFr')
                  }}<span
                    v-if="transName && nameLang !== 'fr'"
                    class="cpp__translating-dot"
                /></label>
                <input
                  v-model="nameFr"
                  class="cpp__input"
                  :class="[
                    nameLang === 'fr' ? 'cpp__input--primary' : '',
                    { 'cpp__input--error': showNameError },
                  ]"
                  type="text"
                  :placeholder="t('product.nameFrPlaceholder')"
                  @blur="onNameBlur('fr')"
                />
                <p v-if="showNameError" class="cpp__field-error">
                  {{ t('product.requiredField') }}
                </p>
              </div>
              <div class="cpp__field">
                <label class="cpp__label"
                  ><span
                    class="cpp__lang-badge"
                    :class="nameLang === 'en' ? 'cpp__lang-badge--active' : ''"
                    >EN</span
                  >
                  English<span
                    v-if="transName && nameLang !== 'en'"
                    class="cpp__translating-dot"
                /></label>
                <input
                  v-model="nameEn"
                  class="cpp__input"
                  :class="nameLang === 'en' ? 'cpp__input--primary' : ''"
                  type="text"
                  placeholder="Name in English…"
                  @blur="onNameBlur('en')"
                />
              </div>
              <div class="cpp__field">
                <label class="cpp__label"
                  ><span
                    class="cpp__lang-badge"
                    :class="nameLang === 'nl' ? 'cpp__lang-badge--active' : ''"
                    >NL</span
                  >
                  Nederlands<span
                    v-if="transName && nameLang !== 'nl'"
                    class="cpp__translating-dot"
                /></label>
                <input
                  v-model="nameNl"
                  class="cpp__input"
                  :class="nameLang === 'nl' ? 'cpp__input--primary' : ''"
                  type="text"
                  placeholder="Naam…"
                  @blur="onNameBlur('nl')"
                />
              </div>
            </div>
          </div>
        </section>

        <div class="cpp__divider" />

        <!-- ════════════════════════════════════════════════════════════
           3. PRIX & TYPE
      ═══════════════════════════════════════════════════════════════ -->
        <section class="cpp__section">
          <div class="cpp__section-hd">
            <div class="cpp__section-ico"><i class="pi pi-euro" /></div>
            <div>
              <p class="cpp__section-title">{{ t('product.priceAndType') }}</p>
              <p class="cpp__section-sub">
                {{ t('product.basePriceSubtitle') }}
              </p>
            </div>
          </div>
          <div class="cpp__fields">
            <div class="cpp__field-row cpp__field-row--3">
              <div class="cpp__field">
                <label class="cpp__label"
                  >{{ t('product.price') }}
                  <span class="cpp__req">*</span></label
                >
                <div class="cpp__price-wrap">
                  <input
                    v-model="price"
                    class="cpp__input cpp__input--price"
                    :class="{ 'cpp__input--error': showPriceError }"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                  />
                  <span class="cpp__price-unit">€</span>
                </div>
                <p v-if="showPriceError" class="cpp__field-error">
                  {{ t('product.requiredField') }}
                </p>
              </div>
              <div class="cpp__field">
                <label class="cpp__label">{{ t('product.priceUnit') }}</label>
                <div class="cpp__select-wrap">
                  <select v-model="priceUnit" class="cpp__select">
                    <option
                      v-for="o in PRICE_UNIT_OPTIONS"
                      :key="o.value"
                      :value="o.value"
                    >
                      {{ o.label }}
                    </option>
                  </select>
                  <i class="pi pi-chevron-down cpp__select-icon" />
                </div>
              </div>
              <div class="cpp__field">
                <label class="cpp__label">{{
                  t('product.prepTimeLabel')
                }}</label>
                <input
                  v-model="preparationTime"
                  class="cpp__input"
                  type="number"
                  min="0"
                  :placeholder="t('product.prepTimePlaceholder')"
                />
              </div>
            </div>
            <div class="cpp__field">
              <label class="cpp__label">{{ t('product.productType') }}</label>
              <div class="cpp__type-chips">
                <button
                  v-for="pt in PRODUCT_TYPES"
                  :key="pt.value"
                  type="button"
                  class="cpp__type-chip"
                  :class="{
                    'cpp__type-chip--active': productType === pt.value,
                  }"
                  @click="productType = pt.value"
                >
                  {{ pt.label }}
                </button>
              </div>
            </div>

            <!-- TVA -->
            <div class="cpp__field">
              <label class="cpp__label">{{ t('product.tvaLabel') }}</label>
              <div class="cpp__dd" @click.stop>
                <button
                  type="button"
                  class="cpp__dd-trigger"
                  :class="{ 'cpp__dd-trigger--active': !!tvaId }"
                  @click="tvaDropdownOpen = !tvaDropdownOpen"
                >
                  <span :class="tvaId ? '' : 'cpp__dd-placeholder'">{{
                    selectedTaxRate
                      ? `${selectedTaxRate.name} — ${selectedTaxRate.rate}%`
                      : loadingTaxRates
                        ? t('product.tvaLoading')
                        : t('product.tvaPlaceholder')
                  }}</span>
                  <i
                    class="pi pi-chevron-down cpp__dd-arrow"
                    :class="{ 'cpp__dd-arrow--open': tvaDropdownOpen }"
                  />
                </button>
                <div v-if="tvaDropdownOpen" class="cpp__dd-panel">
                  <div class="cpp__dd-search-wrap">
                    <i class="pi pi-search" />
                    <input
                      v-model="tvaSearch"
                      class="cpp__dd-search"
                      :placeholder="t('product.tvaSearch')"
                      @click.stop
                    />
                  </div>
                  <ul class="cpp__dd-list">
                    <li
                      v-for="tx in filteredTaxRates"
                      :key="tx.id"
                      class="cpp__dd-item"
                      :class="{ 'cpp__dd-item--active': tvaId === tx.id }"
                      @click="selectTvaId(tx.id)"
                    >
                      {{ tx.name }} — {{ tx.rate }}%
                    </li>
                    <li
                      v-if="!loadingTaxRates && filteredTaxRates.length === 0"
                      class="cpp__dd-empty"
                    >
                      {{ t('product.tvaNoResult') }}
                    </li>
                  </ul>
                </div>
              </div>
              <p class="cpp__hint">{{ t('product.tvaHint') }}</p>
            </div>
          </div>
        </section>

        <div class="cpp__divider" />

        <!-- ════════════════════════════════════════════════════════════
           4. CATÉGORIES
      ═══════════════════════════════════════════════════════════════ -->
        <section class="cpp__section">
          <div class="cpp__section-hd">
            <div class="cpp__section-ico"><i class="pi pi-folder" /></div>
            <div>
              <p class="cpp__section-title">
                {{ t('product.categoriesTitle') }}
              </p>
              <p class="cpp__section-sub">
                {{ t('product.categoriesSubtitle') }}
              </p>
            </div>
            <button
              class="cpp__add-btn"
              :disabled="showCatForm"
              @click="openNewCat"
            >
              <i class="pi pi-plus" /> {{ t('product.newCategory') }}
            </button>
          </div>

          <!-- Formulaire inline créer / éditer catégorie -->
          <div v-if="showCatForm" class="cpp__cat-form">
            <p class="cpp__cat-form-title">
              {{
                editCatId
                  ? t('product.editCategoryTitle')
                  : t('product.newCategoryTitle')
              }}
            </p>

            <!-- Langue de saisie -->
            <div class="cpp__translabel-row">
              <label class="cpp__label">{{ t('product.inputLang') }}</label>
              <div class="cpp__select-wrap" style="max-width: 10rem">
                <select
                  v-model="catFormLang"
                  class="cpp__select cpp__select--sm"
                >
                  <option value="fr">Français</option>
                  <option value="en">English</option>
                  <option value="nl">Nederlands</option>
                </select>
                <i class="pi pi-chevron-down cpp__select-icon" />
              </div>
            </div>

            <!-- Désignation 3 langues -->
            <div class="cpp__cat-form-label">
              <span class="cpp__label"
                >{{ t('product.catDesignation') }}
                <span class="cpp__req">*</span></span
              >
              <span v-if="transNameCat" class="cpp__translating-dot" />
            </div>
            <div class="cpp__field-row cpp__field-row--3">
              <div class="cpp__field">
                <label class="cpp__label"
                  ><span
                    class="cpp__lang-badge"
                    :class="
                      catFormLang === 'fr' ? 'cpp__lang-badge--active' : ''
                    "
                    >FR</span
                  ></label
                >
                <input
                  v-model="catNameFr"
                  class="cpp__input"
                  :class="catFormLang === 'fr' ? 'cpp__input--primary' : ''"
                  type="text"
                  :placeholder="t('product.nameFrPlaceholder')"
                  @blur="onCatNameBlur('fr')"
                />
              </div>
              <div class="cpp__field">
                <label class="cpp__label"
                  ><span
                    class="cpp__lang-badge"
                    :class="
                      catFormLang === 'en' ? 'cpp__lang-badge--active' : ''
                    "
                    >EN</span
                  ></label
                >
                <input
                  v-model="catNameEn"
                  class="cpp__input"
                  :class="catFormLang === 'en' ? 'cpp__input--primary' : ''"
                  type="text"
                  placeholder="Name in English…"
                  @blur="onCatNameBlur('en')"
                />
              </div>
              <div class="cpp__field">
                <label class="cpp__label"
                  ><span
                    class="cpp__lang-badge"
                    :class="
                      catFormLang === 'nl' ? 'cpp__lang-badge--active' : ''
                    "
                    >NL</span
                  ></label
                >
                <input
                  v-model="catNameNl"
                  class="cpp__input"
                  :class="catFormLang === 'nl' ? 'cpp__input--primary' : ''"
                  type="text"
                  placeholder="Naam…"
                  @blur="onCatNameBlur('nl')"
                />
              </div>
            </div>

            <!-- Description 3 langues -->
            <div class="cpp__cat-form-label" style="margin-top: 0.5rem">
              <span class="cpp__label">{{
                t('product.descriptionOptional')
              }}</span>
              <span v-if="transDescCat" class="cpp__translating-dot" />
            </div>
            <div class="cpp__field-row cpp__field-row--3">
              <div class="cpp__field">
                <label class="cpp__label"
                  ><span
                    class="cpp__lang-badge"
                    :class="
                      catFormLang === 'fr' ? 'cpp__lang-badge--active' : ''
                    "
                    >FR</span
                  ></label
                >
                <input
                  v-model="catDescFr"
                  class="cpp__input"
                  :class="catFormLang === 'fr' ? 'cpp__input--primary' : ''"
                  type="text"
                  :placeholder="t('product.descFrPlaceholder')"
                  @blur="onCatDescBlur('fr')"
                />
              </div>
              <div class="cpp__field">
                <label class="cpp__label"
                  ><span
                    class="cpp__lang-badge"
                    :class="
                      catFormLang === 'en' ? 'cpp__lang-badge--active' : ''
                    "
                    >EN</span
                  ></label
                >
                <input
                  v-model="catDescEn"
                  class="cpp__input"
                  :class="catFormLang === 'en' ? 'cpp__input--primary' : ''"
                  type="text"
                  placeholder="Description EN…"
                  @blur="onCatDescBlur('en')"
                />
              </div>
              <div class="cpp__field">
                <label class="cpp__label"
                  ><span
                    class="cpp__lang-badge"
                    :class="
                      catFormLang === 'nl' ? 'cpp__lang-badge--active' : ''
                    "
                    >NL</span
                  ></label
                >
                <input
                  v-model="catDescNl"
                  class="cpp__input"
                  :class="catFormLang === 'nl' ? 'cpp__input--primary' : ''"
                  type="text"
                  placeholder="Beschrijving…"
                  @blur="onCatDescBlur('nl')"
                />
              </div>
            </div>

            <div class="cpp__cat-form-label" style="margin-top: 0.5rem">
              <span class="cpp__label">{{
                t('allCollections.modulesLabel')
              }}</span>
            </div>
            <div class="cpp__cat-modules">
              <button
                v-for="m in CATEGORY_MODULE_OPTIONS"
                :key="m.id"
                type="button"
                class="cpp__cat-module-chip"
                :class="{
                  'cpp__cat-module-chip--on': catModules.includes(m.id),
                }"
                @click="toggleCatModule(m.id)"
              >
                <i :class="['pi', m.icon]" />
                {{ t(m.labelKey) }}
              </button>
            </div>

            <div
              v-if="catError"
              class="cpp__error-banner"
              style="margin-top: 0.25rem"
            >
              <i class="pi pi-exclamation-circle" /> {{ catError }}
            </div>

            <div class="cpp__cat-form-actions">
              <button
                class="cpp__btn cpp__btn--ghost"
                @click="closeCatForm"
                :disabled="catSaving"
              >
                <i class="pi pi-times" /> {{ t('product.cancel') }}
              </button>
              <button
                class="cpp__btn cpp__btn--primary"
                @click="saveCat"
                :disabled="catSaving || !catNameFr.trim()"
              >
                <span
                  v-if="catSaving"
                  class="cpp__spinner cpp__spinner--white"
                />
                <i v-else class="pi pi-check" />
                {{
                  catSaving
                    ? t('product.saving')
                    : editCatId
                      ? t('product.edit')
                      : t('product.create')
                }}
              </button>
            </div>
          </div>

          <!-- Liste des catégories -->
          <div v-if="loadingCats" class="cpp__cats-loading">
            <span class="cpp__spinner" /> {{ t('product.loading') }}
          </div>
          <div v-else-if="categories.length" class="cpp__cat-list">
            <div
              v-for="cat in categories"
              :key="cat.id"
              class="cpp__cat-row"
              :class="{
                'cpp__cat-row--selected': selectedCats.includes(cat.id),
              }"
            >
              <!-- Zone cliquable pour sélectionner -->
              <button class="cpp__cat-select" @click="toggleCat(cat.id)">
                <span
                  class="cpp__cat-check"
                  :class="{
                    'cpp__cat-check--active': selectedCats.includes(cat.id),
                  }"
                >
                  <i v-if="selectedCats.includes(cat.id)" class="pi pi-check" />
                </span>
                <span class="cpp__cat-name">{{ catLabel(cat) }}</span>
              </button>

              <!-- Actions -->
              <div class="cpp__cat-actions">
                <button
                  class="cpp__icon-btn"
                  :disabled="showCatForm"
                  @click.stop="openEditCat(cat)"
                  :title="t('product.edit')"
                >
                  <i class="pi pi-pencil" />
                </button>
                <button
                  class="cpp__icon-btn cpp__icon-btn--danger"
                  @click.stop="confirmDelCat = cat"
                  :title="t('product.delete')"
                >
                  <i class="pi pi-trash" />
                </button>
              </div>
            </div>
          </div>
          <p v-else-if="!showCatForm" class="cpp__cats-empty">
            {{ t('product.noCategoriesHint') }}
          </p>
        </section>

        <div class="cpp__divider" />

        <!-- ════════════════════════════════════════════════════════════
           5. DESCRIPTION
      ═══════════════════════════════════════════════════════════════ -->
        <section class="cpp__section">
          <div class="cpp__section-hd">
            <div class="cpp__section-ico"><i class="pi pi-align-left" /></div>
            <div>
              <p class="cpp__section-title">
                {{ t('product.descriptionOptional') }}
              </p>
              <p class="cpp__section-sub">
                {{ t('product.descriptionSubtitle') }}
              </p>
            </div>
          </div>
          <div class="cpp__fields">
            <div class="cpp__translabel-row">
              <label class="cpp__label">{{ t('product.inputLang') }}</label>
              <div class="cpp__select-wrap" style="max-width: 10rem">
                <select v-model="descLang" class="cpp__select cpp__select--sm">
                  <option value="fr">Français</option>
                  <option value="en">English</option>
                  <option value="nl">Nederlands</option>
                </select>
                <i class="pi pi-chevron-down cpp__select-icon" />
              </div>
            </div>
            <div class="cpp__field-row cpp__field-row--3">
              <div class="cpp__field">
                <label class="cpp__label"
                  ><span
                    class="cpp__lang-badge"
                    :class="descLang === 'fr' ? 'cpp__lang-badge--active' : ''"
                    >FR</span
                  ><span
                    v-if="transDesc && descLang !== 'fr'"
                    class="cpp__translating-dot"
                /></label>
                <textarea
                  v-model="descFr"
                  class="cpp__textarea"
                  :class="descLang === 'fr' ? 'cpp__textarea--primary' : ''"
                  rows="3"
                  :placeholder="t('product.descFrAreaPlaceholder')"
                  @blur="onDescBlur('fr')"
                />
              </div>
              <div class="cpp__field">
                <label class="cpp__label"
                  ><span
                    class="cpp__lang-badge"
                    :class="descLang === 'en' ? 'cpp__lang-badge--active' : ''"
                    >EN</span
                  ><span
                    v-if="transDesc && descLang !== 'en'"
                    class="cpp__translating-dot"
                /></label>
                <textarea
                  v-model="descEn"
                  class="cpp__textarea"
                  :class="descLang === 'en' ? 'cpp__textarea--primary' : ''"
                  rows="3"
                  placeholder="Description in English…"
                  @blur="onDescBlur('en')"
                />
              </div>
              <div class="cpp__field">
                <label class="cpp__label"
                  ><span
                    class="cpp__lang-badge"
                    :class="descLang === 'nl' ? 'cpp__lang-badge--active' : ''"
                    >NL</span
                  ><span
                    v-if="transDesc && descLang !== 'nl'"
                    class="cpp__translating-dot"
                /></label>
                <textarea
                  v-model="descNl"
                  class="cpp__textarea"
                  :class="descLang === 'nl' ? 'cpp__textarea--primary' : ''"
                  rows="3"
                  placeholder="Beschrijving…"
                  @blur="onDescBlur('nl')"
                />
              </div>
            </div>
          </div>
        </section>

        <div class="cpp__divider" />

        <!-- ════════════════════════════════════════════════════════════
           6. VARIANTES
      ═══════════════════════════════════════════════════════════════ -->
        <section class="cpp__section">
          <div class="cpp__section-hd">
            <div class="cpp__section-ico"><i class="pi pi-list" /></div>
            <div>
              <p class="cpp__section-title">
                {{ t('product.variantsOptional') }}
              </p>
              <p class="cpp__section-sub">
                {{ t('product.variantsSubtitle') }}
              </p>
            </div>
            <button class="cpp__add-btn" @click="addVariant">
              <i class="pi pi-plus" /> {{ t('product.add') }}
            </button>
          </div>

          <div v-if="variants.length" class="cpp__variants">
            <div v-for="(v, i) in variants" :key="i" class="cpp__variant-card">
              <!-- En-tête variante -->
              <div class="cpp__variant-header">
                <span class="cpp__variant-num">{{ i + 1 }}</span>
                <div class="cpp__select-wrap" style="max-width: 9rem">
                  <select
                    v-model="v.labelLang"
                    class="cpp__select cpp__select--sm"
                  >
                    <option value="fr">Français</option>
                    <option value="en">English</option>
                    <option value="nl">Nederlands</option>
                  </select>
                  <i class="pi pi-chevron-down cpp__select-icon" />
                </div>
                <span
                  v-if="v.translating"
                  class="cpp__translating-dot"
                  style="margin-left: 0.25rem"
                />
                <button
                  class="cpp__icon-btn cpp__icon-btn--danger"
                  style="margin-left: auto"
                  @click="removeVariant(i)"
                >
                  <i class="pi pi-trash" />
                </button>
              </div>

              <!-- Labels FR / EN / NL -->
              <div class="cpp__field-row cpp__field-row--3">
                <div class="cpp__field">
                  <label class="cpp__label"
                    ><span
                      class="cpp__lang-badge"
                      :class="
                        v.labelLang === 'fr' ? 'cpp__lang-badge--active' : ''
                      "
                      >FR</span
                    ></label
                  >
                  <input
                    v-model="v.labelFr"
                    class="cpp__input"
                    :class="v.labelLang === 'fr' ? 'cpp__input--primary' : ''"
                    type="text"
                    :placeholder="t('product.variantFrPlaceholder')"
                    @blur="onVariantLabelBlur(i, 'fr')"
                  />
                </div>
                <div class="cpp__field">
                  <label class="cpp__label"
                    ><span
                      class="cpp__lang-badge"
                      :class="
                        v.labelLang === 'en' ? 'cpp__lang-badge--active' : ''
                      "
                      >EN</span
                    ></label
                  >
                  <input
                    v-model="v.labelEn"
                    class="cpp__input"
                    :class="v.labelLang === 'en' ? 'cpp__input--primary' : ''"
                    type="text"
                    placeholder="ex: Large"
                    @blur="onVariantLabelBlur(i, 'en')"
                  />
                </div>
                <div class="cpp__field">
                  <label class="cpp__label"
                    ><span
                      class="cpp__lang-badge"
                      :class="
                        v.labelLang === 'nl' ? 'cpp__lang-badge--active' : ''
                      "
                      >NL</span
                    ></label
                  >
                  <input
                    v-model="v.labelNl"
                    class="cpp__input"
                    :class="v.labelLang === 'nl' ? 'cpp__input--primary' : ''"
                    type="text"
                    placeholder="ex: Groot"
                    @blur="onVariantLabelBlur(i, 'nl')"
                  />
                </div>
              </div>

              <!-- Prix + Statut -->
              <div class="cpp__field-row cpp__field-row--2">
                <div class="cpp__field">
                  <label class="cpp__label">{{ t('product.priceEur') }}</label>
                  <div class="cpp__price-wrap">
                    <input
                      v-model="v.price"
                      class="cpp__input cpp__input--price"
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                    />
                    <span class="cpp__price-unit">€</span>
                  </div>
                </div>
                <div class="cpp__field">
                  <label class="cpp__label">{{
                    t('product.statusLabel')
                  }}</label>
                  <div class="cpp__select-wrap">
                    <select v-model="v.status" class="cpp__select">
                      <option value="active">
                        {{ t('product.statusActive') }}
                      </option>
                      <option value="inactive">
                        {{ t('product.statusInactive') }}
                      </option>
                      <option value="out-of-stock">
                        {{ t('product.statusOutOfStock') }}
                      </option>
                    </select>
                    <i class="pi pi-chevron-down cpp__select-icon" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="cpp__empty">
            <i class="pi pi-list cpp__empty-icon" />
            <p>{{ t('product.noVariantsHint') }}</p>
          </div>
        </section>

        <div class="cpp__divider" />

        <!-- ════════════════════════════════════════════════════════════
           7. GROUPES D'OPTIONS (AddonGroups)
      ═══════════════════════════════════════════════════════════════ -->
        <section class="cpp__section">
          <div class="cpp__section-hd">
            <div class="cpp__section-ico"><i class="pi pi-th-large" /></div>
            <div>
              <p class="cpp__section-title">
                {{ t('product.addonGroupsOptional') }}
              </p>
              <p class="cpp__section-sub">
                {{ t('product.addonGroupsSubtitle') }}
              </p>
            </div>
            <button class="cpp__add-btn" @click="addGroup">
              <i class="pi pi-plus" /> {{ t('product.addGroup') }}
            </button>
          </div>

          <div v-if="addonGroups.length" class="cpp__groups">
            <div
              v-for="(g, gi) in addonGroups"
              :key="gi"
              class="cpp__group-card"
            >
              <!-- En-tête groupe -->
              <div class="cpp__group-head">
                <div class="cpp__group-num">{{ gi + 1 }}</div>
                <div class="cpp__group-head-fields">
                  <!-- Titre multilingue -->
                  <div class="cpp__fields" style="flex: 1">
                    <div class="cpp__translabel-row">
                      <label class="cpp__label" style="font-size: 0.75rem">{{
                        t('product.groupTitle')
                      }}</label>
                      <div class="cpp__select-wrap" style="max-width: 9rem">
                        <select
                          v-model="g.titleLang"
                          class="cpp__select cpp__select--sm"
                        >
                          <option value="fr">Français</option>
                          <option value="en">English</option>
                          <option value="nl">Nederlands</option>
                        </select>
                        <i class="pi pi-chevron-down cpp__select-icon" />
                      </div>
                      <span v-if="g.translating" class="cpp__translating-dot" />
                    </div>
                    <div class="cpp__field-row cpp__field-row--3">
                      <div class="cpp__field">
                        <label class="cpp__label"
                          ><span
                            class="cpp__lang-badge"
                            :class="
                              g.titleLang === 'fr'
                                ? 'cpp__lang-badge--active'
                                : ''
                            "
                            >FR</span
                          ></label
                        >
                        <input
                          v-model="g.titleFr"
                          class="cpp__input"
                          :class="
                            g.titleLang === 'fr' ? 'cpp__input--primary' : ''
                          "
                          type="text"
                          placeholder="ex: Sauces"
                          @blur="onGroupTitleBlur(gi, 'fr')"
                        />
                      </div>
                      <div class="cpp__field">
                        <label class="cpp__label"
                          ><span
                            class="cpp__lang-badge"
                            :class="
                              g.titleLang === 'en'
                                ? 'cpp__lang-badge--active'
                                : ''
                            "
                            >EN</span
                          ></label
                        >
                        <input
                          v-model="g.titleEn"
                          class="cpp__input"
                          :class="
                            g.titleLang === 'en' ? 'cpp__input--primary' : ''
                          "
                          type="text"
                          placeholder="ex: Sauces"
                          @blur="onGroupTitleBlur(gi, 'en')"
                        />
                      </div>
                      <div class="cpp__field">
                        <label class="cpp__label"
                          ><span
                            class="cpp__lang-badge"
                            :class="
                              g.titleLang === 'nl'
                                ? 'cpp__lang-badge--active'
                                : ''
                            "
                            >NL</span
                          ></label
                        >
                        <input
                          v-model="g.titleNl"
                          class="cpp__input"
                          :class="
                            g.titleLang === 'nl' ? 'cpp__input--primary' : ''
                          "
                          type="text"
                          placeholder="ex: Sauzen"
                          @blur="onGroupTitleBlur(gi, 'nl')"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <button
                  class="cpp__icon-btn cpp__icon-btn--danger"
                  style="align-self: flex-start; margin-top: 0.25rem"
                  @click="removeGroup(gi)"
                >
                  <i class="pi pi-trash" />
                </button>
              </div>

              <!-- Config groupe -->
              <div class="cpp__field-row cpp__field-row--4">
                <div class="cpp__field">
                  <label class="cpp__label">{{ t('product.extraType') }}</label>
                  <div class="cpp__select-wrap">
                    <select v-model="g.extraType" class="cpp__select">
                      <option value="supplements">
                        {{ t('product.typeSupplement') }}
                      </option>
                      <option value="side-dish">
                        {{ t('product.typeSideDish') }}
                      </option>
                      <option value="proposal">
                        {{ t('product.typeProposal') }}
                      </option>
                    </select>
                    <i class="pi pi-chevron-down cpp__select-icon" />
                  </div>
                </div>
                <div class="cpp__field">
                  <label class="cpp__label">{{
                    t('product.choiceType')
                  }}</label>
                  <div class="cpp__select-wrap">
                    <select v-model="g.chooseType" class="cpp__select">
                      <option value="multiple">
                        {{ t('product.multiple') }}
                      </option>
                      <option value="unique">{{ t('product.unique') }}</option>
                    </select>
                    <i class="pi pi-chevron-down cpp__select-icon" />
                  </div>
                </div>
                <div class="cpp__field">
                  <label class="cpp__label">{{ t('product.qtyMinMax') }}</label>
                  <div class="cpp__qty-row">
                    <input
                      v-model="g.minQty"
                      class="cpp__input cpp__input--qty"
                      type="number"
                      min="0"
                      placeholder="0"
                    />
                    <span class="cpp__qty-sep">/</span>
                    <input
                      v-model="g.maxQty"
                      class="cpp__input cpp__input--qty"
                      type="number"
                      min="1"
                      placeholder="1"
                    />
                  </div>
                </div>
                <div class="cpp__field cpp__field--center">
                  <label class="cpp__label">{{
                    t('product.requiredLabel')
                  }}</label>
                  <label class="cpp__toggle">
                    <input
                      type="checkbox"
                      v-model="g.required"
                      class="cpp__toggle-input"
                    />
                    <span class="cpp__toggle-track"
                      ><span class="cpp__toggle-thumb"
                    /></span>
                  </label>
                </div>
              </div>

              <!-- Options du groupe -->
              <div class="cpp__group-options-head">
                <span class="cpp__group-options-label">Options</span>
                <button
                  class="cpp__add-btn cpp__add-btn--sm"
                  @click="addOption(gi)"
                >
                  <i class="pi pi-plus" /> Option
                </button>
              </div>

              <div class="cpp__group-options">
                <div
                  v-for="(opt, oi) in g.options"
                  :key="oi"
                  class="cpp__option-row"
                >
                  <div class="cpp__option-num">{{ oi + 1 }}</div>
                  <div
                    style="
                      flex: 1;
                      display: flex;
                      flex-direction: column;
                      gap: 0.625rem;
                      min-width: 0;
                    "
                  >
                    <!-- Langue de saisie option -->
                    <div
                      style="
                        display: flex;
                        align-items: center;
                        gap: 0.625rem;
                        flex-wrap: wrap;
                      "
                    >
                      <span class="cpp__label" style="font-size: 0.75rem">{{
                        t('product.language')
                      }}</span>
                      <div class="cpp__select-wrap" style="max-width: 9rem">
                        <select
                          v-model="opt.optLang"
                          class="cpp__select cpp__select--sm"
                        >
                          <option value="fr">Français</option>
                          <option value="en">English</option>
                          <option value="nl">Nederlands</option>
                        </select>
                        <i class="pi pi-chevron-down cpp__select-icon" />
                      </div>
                      <span
                        v-if="opt.translating"
                        class="cpp__translating-dot"
                      />
                    </div>
                    <!-- Contenus FR / EN / NL -->
                    <div class="cpp__field-row cpp__field-row--3">
                      <div class="cpp__field">
                        <label class="cpp__label"
                          ><span
                            class="cpp__lang-badge"
                            :class="
                              opt.optLang === 'fr'
                                ? 'cpp__lang-badge--active'
                                : ''
                            "
                            >FR</span
                          ></label
                        >
                        <input
                          v-model="opt.contentFr"
                          class="cpp__input"
                          :class="
                            opt.optLang === 'fr' ? 'cpp__input--primary' : ''
                          "
                          type="text"
                          placeholder="ex: Ketchup"
                          @blur="onOptionBlur(gi, oi, 'fr')"
                        />
                      </div>
                      <div class="cpp__field">
                        <label class="cpp__label"
                          ><span
                            class="cpp__lang-badge"
                            :class="
                              opt.optLang === 'en'
                                ? 'cpp__lang-badge--active'
                                : ''
                            "
                            >EN</span
                          ></label
                        >
                        <input
                          v-model="opt.contentEn"
                          class="cpp__input"
                          :class="
                            opt.optLang === 'en' ? 'cpp__input--primary' : ''
                          "
                          type="text"
                          placeholder="ex: Ketchup"
                          @blur="onOptionBlur(gi, oi, 'en')"
                        />
                      </div>
                      <div class="cpp__field">
                        <label class="cpp__label"
                          ><span
                            class="cpp__lang-badge"
                            :class="
                              opt.optLang === 'nl'
                                ? 'cpp__lang-badge--active'
                                : ''
                            "
                            >NL</span
                          ></label
                        >
                        <input
                          v-model="opt.contentNl"
                          class="cpp__input"
                          :class="
                            opt.optLang === 'nl' ? 'cpp__input--primary' : ''
                          "
                          type="text"
                          placeholder="ex: Ketchup"
                          @blur="onOptionBlur(gi, oi, 'nl')"
                        />
                      </div>
                    </div>
                  </div>
                  <div class="cpp__field cpp__option-price">
                    <label class="cpp__label">{{
                      t('product.priceEur')
                    }}</label>
                    <div class="cpp__price-wrap">
                      <input
                        v-model="opt.price"
                        class="cpp__input cpp__input--price"
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                      />
                      <span class="cpp__price-unit">€</span>
                    </div>
                  </div>
                  <button
                    v-if="g.options.length > 1"
                    class="cpp__icon-btn cpp__icon-btn--danger"
                    style="align-self: flex-end; margin-bottom: 0"
                    @click="removeOption(gi, oi)"
                  >
                    <i class="pi pi-trash" />
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="cpp__empty">
            <i class="pi pi-th-large cpp__empty-icon" />
            <p>{{ t('product.noGroupsHint') }}</p>
          </div>
        </section>

        <!-- Erreur -->
        <div v-if="error" class="cpp__error-banner">
          <i class="pi pi-exclamation-circle" /> {{ error }}
        </div>

        <!-- Actions -->
        <div class="cpp__actions">
          <button class="cpp__btn cpp__btn--ghost" @click="router.back()">
            <i class="pi pi-times" /> {{ t('product.cancel') }}
          </button>
          <button
            class="cpp__btn cpp__btn--primary"
            :disabled="saving || imgUploading"
            @click="submit"
          >
            <span
              v-if="saving || imgUploading"
              class="cpp__spinner cpp__spinner--white"
            />
            <i v-else class="pi pi-check" />
            {{
              imgUploading
                ? t('product.uploadingImage')
                : saving
                  ? t('product.creating')
                  : t('product.createProduct')
            }}
          </button>
        </div>
      </div>
    </template>

    <!-- Modale suppression catégorie -->
    <Teleport to="body">
      <Transition name="cpp-modal">
        <div
          v-if="confirmDelCat"
          class="cpp__modal-overlay"
          @click.self="deletingCat ? null : (confirmDelCat = null)"
        >
          <div class="cpp__modal">
            <div class="cpp__modal-icon">
              <i class="pi pi-exclamation-triangle" />
            </div>
            <h3 class="cpp__modal-title">
              {{ t('product.deleteCategoryConfirm') }}
            </h3>
            <p class="cpp__modal-desc">
              <strong>« {{ catLabel(confirmDelCat) }} »</strong>
              {{ t('product.categoryWillBeDeleted') }}
            </p>
            <div class="cpp__modal-actions">
              <button
                class="cpp__btn cpp__btn--ghost"
                @click="confirmDelCat = null"
                :disabled="deletingCat"
              >
                <i class="pi pi-times" /> {{ t('product.cancel') }}
              </button>
              <button
                class="cpp__btn cpp__btn--danger"
                @click="deleteCat"
                :disabled="deletingCat"
              >
                <span
                  v-if="deletingCat"
                  class="cpp__spinner cpp__spinner--white"
                /><i v-else class="pi pi-trash" />
                {{ deletingCat ? t('product.deleting') : t('product.delete') }}
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<style scoped>
.cpp {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  min-width: 0;
  padding: 2.5rem 3rem;
  box-sizing: border-box;
}

.cpp__bc {
  display: flex;
}
.cpp__bc-link {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: 0.8125rem;
  font-family: inherit;
  color: var(--nxt-on-surface-muted);
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  transition: color 0.15s;
}
.cpp__bc-link:hover {
  color: var(--nxt-primary);
}

.cpp__header {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  background: var(--nxt-card-bg);
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 1.25rem;
  padding: 1.5rem;
  box-shadow: var(--nxt-shadow-sm);
}
.cpp__header-icon {
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 0.75rem;
  background: rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.125rem;
  color: var(--nxt-primary);
  flex-shrink: 0;
}
.cpp__title {
  margin: 0 0 0.2rem;
  font-size: 1.25rem;
  font-weight: 800;
  letter-spacing: -0.025em;
  color: var(--nxt-on-surface);
}
.cpp__sub {
  margin: 0;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
}

.cpp__body {
  background: var(--nxt-card-bg);
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 1.25rem;
  box-shadow: var(--nxt-shadow-sm);
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
}
.cpp__divider {
  height: 1px;
  background: var(--nxt-outline-variant);
}

/* Sections */
.cpp__section {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}
.cpp__section-hd {
  display: flex;
  align-items: flex-start;
  gap: 0.875rem;
}
.cpp__section-ico {
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 0.625rem;
  flex-shrink: 0;
  background: var(--nxt-surface-mid);
  border: 1px solid var(--nxt-outline-variant);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9375rem;
  color: var(--nxt-on-surface-variant);
}
.cpp__section-title {
  margin: 0 0 0.1rem;
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
}
.cpp__section-sub {
  margin: 0;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
}
.cpp__req {
  color: var(--nxt-error);
}
.cpp__input--error {
  border-color: var(--nxt-error, #dc2626);
}
.cpp__field-error {
  margin: 0;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--nxt-error, #dc2626);
}
.cpp__add-btn {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  height: 2rem;
  padding: 0 0.875rem;
  border-radius: 0.5rem;
  border: 1px solid var(--nxt-primary);
  background: rgba(0, 0, 0, 0.06);
  color: var(--nxt-primary);
  font-size: 0.8125rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: background 0.15s;
}
.cpp__add-btn:hover {
  background: rgba(0, 0, 0, 0.12);
}
.cpp__add-btn--sm {
  margin-left: 0;
  height: 1.75rem;
  padding: 0 0.625rem;
  font-size: 0.75rem;
}

/* Fields */
.cpp__fields {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.cpp__field-row {
  display: grid;
  gap: 0.875rem;
}
.cpp__field-row--2 {
  grid-template-columns: 1fr 1fr;
}
.cpp__field-row--3 {
  grid-template-columns: 1fr 1fr 1fr;
}
.cpp__field-row--4 {
  grid-template-columns: 1fr 1fr 1fr auto;
  align-items: end;
}
@media (max-width: 1023px) {
  .cpp {
    padding: 1.5rem;
  }
}
@media (max-width: 639px) {
  .cpp {
    padding: 1rem;
  }
}
@media (max-width: 800px) {
  .cpp__field-row--4 {
    grid-template-columns: 1fr 1fr;
  }
}
@media (max-width: 700px) {
  .cpp__field-row--3 {
    grid-template-columns: 1fr 1fr;
  }
}
@media (max-width: 500px) {
  .cpp__field-row--2,
  .cpp__field-row--3,
  .cpp__field-row--4 {
    grid-template-columns: 1fr;
  }
}
.cpp__field {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}
.cpp__field--center {
  justify-content: flex-start;
}
.cpp__label {
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--nxt-on-surface);
  display: flex;
  align-items: center;
  gap: 0.375rem;
  flex-wrap: wrap;
}
.cpp__input {
  height: 2.625rem;
  padding: 0 0.875rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.625rem;
  background: var(--nxt-surface-low);
  color: var(--nxt-on-surface);
  font-size: 0.875rem;
  font-family: inherit;
  outline: none;
  transition:
    border-color 0.15s,
    background 0.15s;
  width: 100%;
  box-sizing: border-box;
}
.cpp__input::placeholder {
  color: var(--nxt-on-surface-muted);
}
.cpp__input:focus {
  border-color: var(--nxt-primary);
  background: var(--nxt-card-bg);
}
.cpp__input--primary {
  border-color: var(--nxt-primary);
  background: var(--nxt-card-bg);
}
.cpp__input--qty {
  width: 4.5rem;
  text-align: center;
}
.cpp__textarea {
  padding: 0.625rem 0.875rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.625rem;
  background: var(--nxt-surface-low);
  color: var(--nxt-on-surface);
  font-size: 0.875rem;
  font-family: inherit;
  outline: none;
  resize: vertical;
  width: 100%;
  box-sizing: border-box;
  transition:
    border-color 0.15s,
    background 0.15s;
}
.cpp__textarea::placeholder {
  color: var(--nxt-on-surface-muted);
}
.cpp__textarea:focus {
  border-color: var(--nxt-primary);
  background: var(--nxt-card-bg);
}
.cpp__textarea--primary {
  border-color: var(--nxt-primary);
  background: var(--nxt-card-bg);
}
.cpp__select-wrap {
  position: relative;
}
.cpp__select {
  width: 100%;
  height: 2.625rem;
  padding: 0 2.5rem 0 0.875rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.625rem;
  background: var(--nxt-surface-low);
  color: var(--nxt-on-surface);
  font-size: 0.875rem;
  font-family: inherit;
  outline: none;
  appearance: none;
  cursor: pointer;
  transition: border-color 0.15s;
  box-sizing: border-box;
}
.cpp__select--sm {
  height: 2.125rem;
  font-size: 0.8125rem;
}
.cpp__select:focus {
  border-color: var(--nxt-primary);
}
.cpp__select-icon {
  position: absolute;
  right: 0.875rem;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.75rem;
  color: var(--nxt-on-surface-muted);
  pointer-events: none;
}

.cpp__hint {
  margin: 0.25rem 0 0;
  font-size: 0.75rem;
  color: var(--nxt-on-surface-muted, #999);
  line-height: 1.4;
}

/* Dropdown TVA */
.cpp__dd {
  position: relative;
}
.cpp__dd-trigger {
  width: 100%;
  height: 2.75rem;
  padding: 0 0.875rem;
  border-radius: 0.75rem;
  border: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.1));
  background: var(--nxt-surface-low, #f8f9fa);
  color: var(--nxt-on-surface);
  font-size: 0.875rem;
  font-family: inherit;
  outline: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  transition: border-color 0.15s;
  box-sizing: border-box;
}
.cpp__dd-trigger:hover {
  border-color: var(--nxt-primary, #141c29);
}
.cpp__dd-trigger--active {
  border-color: #0b1320;
  font-weight: 600;
}
[data-theme='dark'] .cpp__dd-trigger--active {
  border-color: #fff;
}
.cpp__dd-placeholder {
  color: var(--nxt-on-surface-muted, #999);
  font-weight: 400;
}
.cpp__dd-arrow {
  font-size: 0.75rem;
  color: var(--nxt-on-surface-variant);
  transition: transform 0.15s;
  flex-shrink: 0;
}
.cpp__dd-arrow--open {
  transform: rotate(180deg);
}
.cpp__dd-panel {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 200;
  background: var(--nxt-card-bg, #fff);
  border: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.1));
  border-radius: 0.75rem;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  overflow: hidden;
}
[data-theme='dark'] .cpp__dd-panel {
  background: #1e2025;
}
.cpp__dd-search-wrap {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 0.875rem;
  border-bottom: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.08));
  color: var(--nxt-on-surface-variant);
}
.cpp__dd-search {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 0.8125rem;
  font-family: inherit;
  color: var(--nxt-on-surface);
}
.cpp__dd-list {
  max-height: 220px;
  overflow-y: auto;
  padding: 0.375rem;
  margin: 0;
  list-style: none;
}
.cpp__dd-item {
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface);
  cursor: pointer;
}
.cpp__dd-item:hover {
  background: var(--nxt-surface-mid, rgba(0, 0, 0, 0.04));
}
.cpp__dd-item--active {
  background: #0b1320;
  color: #fff;
  font-weight: 600;
}
[data-theme='dark'] .cpp__dd-item--active {
  background: #fff;
  color: #0b1320;
}
.cpp__dd-empty {
  padding: 0.75rem;
  font-size: 0.75rem;
  color: var(--nxt-on-surface-muted, #999);
  text-align: center;
}

/* Type chips */
.cpp__type-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}
.cpp__type-chip {
  height: 2.375rem;
  padding: 0 1rem;
  border-radius: 0.625rem;
  border: 1.5px solid var(--nxt-outline-variant);
  background: var(--nxt-surface-low);
  color: var(--nxt-on-surface-variant);
  font-size: 0.875rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
}
.cpp__type-chip:hover {
  border-color: var(--nxt-primary);
  color: var(--nxt-primary);
}
.cpp__type-chip--active {
  background: var(--nxt-primary);
  border-color: var(--nxt-primary);
  color: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

/* Price */
.cpp__price-wrap {
  position: relative;
}
.cpp__input--price {
  padding-right: 2.25rem;
}
.cpp__price-unit {
  position: absolute;
  right: 0.875rem;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--nxt-on-surface-muted);
  pointer-events: none;
}

/* Qty row */
.cpp__qty-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.cpp__qty-sep {
  font-weight: 700;
  color: var(--nxt-on-surface-muted);
}

/* Lang badges */
.cpp__translabel-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}
.cpp__lang-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.25rem;
  border-radius: 0.3rem;
  font-size: 0.65rem;
  font-weight: 800;
  background: var(--nxt-surface-mid);
  color: var(--nxt-on-surface-muted);
  border: 1px solid var(--nxt-outline-variant);
  flex-shrink: 0;
}
.cpp__lang-badge--active {
  background: var(--nxt-primary);
  color: #fff;
  border-color: var(--nxt-primary);
}
.cpp__translating-dot {
  width: 0.45rem;
  height: 0.45rem;
  border-radius: 50%;
  background: var(--nxt-primary);
  display: inline-block;
  animation: cpp-pulse 0.8s ease-in-out infinite;
  flex-shrink: 0;
}
@keyframes cpp-pulse {
  0%,
  100% {
    opacity: 0.3;
  }
  50% {
    opacity: 1;
  }
}

/* Image upload */
.cpp__img-upload {
  display: flex;
  align-items: center;
  gap: 1.25rem;
  flex-wrap: wrap;
}
.cpp__img-preview-wrap {
  width: 7rem;
  height: 7rem;
  flex-shrink: 0;
  border-radius: 0.875rem;
  border: 1.5px solid var(--nxt-outline-variant);
  background: var(--nxt-surface-low);
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}
.cpp__img-preview {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.cpp__img-placeholder {
  font-size: 2rem;
  color: var(--nxt-on-surface-muted);
  opacity: 0.3;
}
.cpp__img-actions {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  flex-wrap: wrap;
}
.cpp__filename {
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
}

/* Categories */
.cpp__cats-loading {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: var(--nxt-on-surface-muted);
}
.cpp__cats {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}
.cpp__cat-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  height: 2rem;
  padding: 0 0.875rem;
  border-radius: 9999px;
  font-size: 0.8125rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  border: 1.5px solid var(--nxt-outline-variant);
  background: var(--nxt-surface-low);
  color: var(--nxt-on-surface-variant);
  transition: all 0.15s;
}
.cpp__cat-chip:hover {
  border-color: var(--nxt-primary);
  color: var(--nxt-primary);
  background: rgba(0, 0, 0, 0.06);
}
.cpp__cat-chip--active {
  border-color: var(--nxt-primary);
  background: rgba(0, 0, 0, 0.1);
  color: var(--nxt-primary);
}
.cpp__cats-empty {
  margin: 0;
  font-size: 0.875rem;
  color: var(--nxt-on-surface-muted);
  font-style: italic;
}

/* Variantes */
.cpp__variants {
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
}
.cpp__variant-card {
  background: var(--nxt-surface-low);
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 1rem;
  padding: 1.125rem;
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
}
.cpp__variant-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.cpp__variant-num {
  width: 1.625rem;
  height: 1.625rem;
  min-width: 1.625rem;
  border-radius: 50%;
  background: var(--nxt-surface-mid);
  color: var(--nxt-on-surface-muted);
  font-size: 0.75rem;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Groups */
.cpp__groups {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}
.cpp__group-card {
  background: var(--nxt-surface-low);
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 1rem;
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.cpp__group-head {
  display: flex;
  align-items: flex-start;
  gap: 0.875rem;
}
.cpp__group-num {
  width: 1.75rem;
  height: 1.75rem;
  min-width: 1.75rem;
  border-radius: 50%;
  background: var(--nxt-primary);
  color: #fff;
  font-size: 0.8125rem;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 0.25rem;
}
.cpp__group-head-fields {
  flex: 1;
  min-width: 0;
}
.cpp__group-options-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 0.25rem;
  border-top: 1px solid var(--nxt-outline-variant);
}
.cpp__group-options-label {
  font-size: 0.8125rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
}
.cpp__group-options {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}
.cpp__option-row {
  display: flex;
  align-items: flex-end;
  gap: 0.75rem;
  padding: 0.75rem;
  background: var(--nxt-card-bg);
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.75rem;
  flex-wrap: wrap;
}
.cpp__option-num {
  width: 1.375rem;
  height: 1.375rem;
  min-width: 1.375rem;
  border-radius: 50%;
  background: var(--nxt-surface-mid);
  color: var(--nxt-on-surface-muted);
  font-size: 0.6875rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.375rem;
}
.cpp__option-price {
  min-width: 7rem;
  max-width: 9rem;
  flex-shrink: 0;
}

/* Toggle */
.cpp__toggle {
  display: inline-flex;
  align-items: center;
  cursor: pointer;
}
.cpp__toggle-input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}
.cpp__toggle-track {
  width: 2.5rem;
  height: 1.375rem;
  border-radius: 9999px;
  background: var(--nxt-outline-variant);
  position: relative;
  transition: background 0.2s;
}
.cpp__toggle-input:checked + .cpp__toggle-track {
  background: var(--nxt-primary);
}
.cpp__toggle-thumb {
  position: absolute;
  top: 0.1875rem;
  left: 0.1875rem;
  width: 1rem;
  height: 1rem;
  border-radius: 50%;
  background: #fff;
  transition: transform 0.2s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}
.cpp__toggle-input:checked + .cpp__toggle-track .cpp__toggle-thumb {
  transform: translateX(1.125rem);
}

/* Icon buttons */
.cpp__icon-btn {
  width: 2rem;
  height: 2rem;
  border-radius: 0.5rem;
  border: 1px solid var(--nxt-outline-variant);
  background: var(--nxt-card-bg);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  color: var(--nxt-on-surface-variant);
  cursor: pointer;
  transition: all 0.15s;
  flex-shrink: 0;
}
.cpp__icon-btn--danger:hover {
  background: var(--nxt-error-container, #fff0f0);
  color: var(--nxt-error);
  border-color: var(--nxt-error);
}

/* Empty */
.cpp__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 2rem;
  color: var(--nxt-on-surface-muted);
  font-size: 0.875rem;
  text-align: center;
}
.cpp__empty-icon {
  font-size: 1.5rem;
  opacity: 0.2;
}
.cpp__empty p {
  margin: 0;
}

/* Buttons */
.cpp__btn {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  height: 2.5rem;
  padding: 0 1.25rem;
  border-radius: 0.625rem;
  font-size: 0.875rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  border: none;
  transition:
    filter 0.15s,
    opacity 0.15s;
}
.cpp__btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.cpp__btn--primary {
  background: var(--nxt-gradient-primary);
  color: var(--nxt-on-primary);
  box-shadow: var(--nxt-gradient-primary-btn-shadow);
}
.cpp__btn--primary:not(:disabled):hover {
  filter: brightness(1.07);
}
.cpp__btn--ghost {
  background: var(--nxt-surface-low);
  color: var(--nxt-on-surface-variant);
  border: 1px solid var(--nxt-outline-variant);
}
.cpp__btn--ghost:hover {
  background: var(--nxt-surface-mid);
}
.cpp__btn--outline {
  background: transparent;
  color: var(--nxt-primary);
  border: 1px solid var(--nxt-primary);
}
.cpp__btn--outline:hover {
  background: rgba(0, 0, 0, 0.07);
}

/* Footer */
.cpp__actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding-top: 1.25rem;
  border-top: 1px solid var(--nxt-outline-variant);
}
.cpp__error-banner {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.875rem 1.125rem;
  border-radius: 0.75rem;
  background: var(--nxt-error-container, #fff0f0);
  border: 1px solid var(--nxt-error, #e53935);
  font-size: 0.875rem;
  color: var(--nxt-error, #e53935);
}

/* Spinner */
.cpp__spinner {
  width: 1rem;
  height: 1rem;
  border-radius: 50%;
  display: inline-block;
  border: 2px solid var(--nxt-outline-variant);
  border-top-color: var(--nxt-primary);
  animation: spin 0.7s linear infinite;
}
.cpp__spinner--white {
  border-color: rgba(255, 255, 255, 0.25);
  border-top-color: var(--nxt-on-primary);
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Success */
.cpp__success {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 3.5rem 2rem;
  background: var(--nxt-card-bg);
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 1.25rem;
  box-shadow: var(--nxt-shadow-sm);
  text-align: center;
}
.cpp__success-icon {
  width: 4rem;
  height: 4rem;
  border-radius: 50%;
  background: rgba(16, 181, 16, 0.08);
  border: 2px solid rgba(16, 181, 16, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: var(--nxt-status-up, #10b510);
}
.cpp__success-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 800;
  color: var(--nxt-on-surface);
}
.cpp__success-sub {
  margin: 0;
  font-size: 0.875rem;
  color: var(--nxt-on-surface-muted);
}
.cpp__success-actions {
  display: flex;
  gap: 0.75rem;
  justify-content: center;
  flex-wrap: wrap;
}

/* Catégorie — formulaire inline */
.cpp__cat-form {
  background: var(--nxt-surface-low);
  border: 1.5px solid var(--nxt-primary);
  border-radius: 1rem;
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
}
.cpp__cat-form-title {
  margin: 0;
  font-size: 0.875rem;
  font-weight: 800;
  color: var(--nxt-primary);
}
.cpp__cat-form-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.cpp__cat-modules {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}
.cpp__cat-module-chip {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 0.75rem;
  border: 1.5px solid var(--nxt-outline-variant);
  border-radius: 0.625rem;
  background: var(--nxt-card-bg);
  color: var(--nxt-on-surface-variant);
  font-size: 0.8125rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
}
.cpp__cat-module-chip--on {
  border-color: #ffd60a;
  background: rgba(255, 214, 10, 0.1);
  color: #0b1320;
}
[data-theme='dark'] .cpp__cat-module-chip--on {
  background: rgba(255, 214, 10, 0.15);
  color: #ffd60a;
}
.cpp__cat-form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  padding-top: 0.75rem;
  border-top: 1px solid var(--nxt-outline-variant);
}

/* Catégorie — liste */
.cpp__cat-list {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}
.cpp__cat-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 0.875rem;
  background: var(--nxt-surface-low);
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.75rem;
  transition:
    border-color 0.15s,
    background 0.15s;
}
.cpp__cat-row--selected {
  background: rgba(0, 0, 0, 0.06);
  border-color: rgba(0, 0, 0, 0.3);
}
.cpp__cat-select {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: inherit;
  text-align: left;
  min-width: 0;
}
.cpp__cat-check {
  width: 1.375rem;
  height: 1.375rem;
  min-width: 1.375rem;
  border-radius: 0.375rem;
  border: 1.5px solid var(--nxt-outline-variant);
  background: var(--nxt-card-bg);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.6875rem;
  color: transparent;
  transition: all 0.15s;
  flex-shrink: 0;
}
.cpp__cat-check--active {
  background: var(--nxt-primary);
  border-color: var(--nxt-primary);
  color: #fff;
}
.cpp__cat-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--nxt-on-surface);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.cpp__cat-actions {
  display: flex;
  gap: 0.375rem;
  flex-shrink: 0;
}

/* Modal */
.cpp__modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(3px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}
.cpp__modal {
  background: var(--nxt-card-bg);
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 1.25rem;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.2);
  padding: 2rem;
  width: 100%;
  max-width: 400px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  text-align: center;
}
.cpp__modal-icon {
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 50%;
  background: rgba(229, 57, 53, 0.08);
  border: 2px solid rgba(229, 57, 53, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: var(--nxt-error, #e53935);
}
.cpp__modal-title {
  margin: 0;
  font-size: 1rem;
  font-weight: 800;
  color: var(--nxt-on-surface);
}
.cpp__modal-desc {
  margin: 0;
  font-size: 0.875rem;
  color: var(--nxt-on-surface-muted);
}
.cpp__modal-desc strong {
  color: var(--nxt-on-surface);
  font-weight: 700;
}
.cpp__modal-actions {
  display: flex;
  gap: 0.625rem;
  justify-content: center;
  width: 100%;
}
.cpp__btn--danger {
  background: var(--nxt-error, #e53935);
  color: #fff;
}
.cpp__btn--danger:not(:disabled):hover {
  filter: brightness(1.1);
}
.cpp-modal-enter-active,
.cpp-modal-leave-active {
  transition:
    opacity 0.18s ease,
    transform 0.18s ease;
}
.cpp-modal-enter-from,
.cpp-modal-leave-to {
  opacity: 0;
}
.cpp-modal-enter-from .cpp__modal,
.cpp-modal-leave-to .cpp__modal {
  transform: scale(0.94) translateY(8px);
}
</style>

<style>
/* ── Dark mode overrides ─────────────────────────────────────────────── */
[data-theme='dark'] .cpp {
  background: #0c1421;
}

/* Cards / containers */
[data-theme='dark'] .cpp__header,
[data-theme='dark'] .cpp__body,
[data-theme='dark'] .cpp__success,
[data-theme='dark'] .cpp__cat-form,
[data-theme='dark'] .cpp__variant-card,
[data-theme='dark'] .cpp__group-card,
[data-theme='dark'] .cpp__option-row,
[data-theme='dark'] .cpp__modal {
  background: rgba(20, 28, 41, 0.8);
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .cpp__modal {
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
}

[data-theme='dark'] .cpp__divider {
  background: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .cpp__actions {
  border-top-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .cpp__group-options-head {
  border-top-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .cpp__cat-form-actions {
  border-top-color: rgba(255, 255, 255, 0.08);
}

/* Header / section icons */
[data-theme='dark'] .cpp__header-icon,
[data-theme='dark'] .cpp__section-ico {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.08);
  color: #ffd60a;
}

[data-theme='dark'] .cpp__bc-link:hover {
  color: #ffd60a;
}

/* Inputs / textareas / selects */
[data-theme='dark'] .cpp__input,
[data-theme='dark'] .cpp__textarea,
[data-theme='dark'] .cpp__select {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
  color: #fff;
}
[data-theme='dark'] .cpp__input:focus,
[data-theme='dark'] .cpp__textarea:focus,
[data-theme='dark'] .cpp__select:focus {
  border-color: #ffd60a;
  background: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .cpp__input--primary,
[data-theme='dark'] .cpp__textarea--primary {
  border-color: #ffd60a;
  background: rgba(255, 214, 10, 0.06);
}

/* Type chips */
[data-theme='dark'] .cpp__type-chip {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.6);
}
[data-theme='dark'] .cpp__type-chip:hover {
  border-color: #ffd60a;
  color: #ffd60a;
}
[data-theme='dark'] .cpp__type-chip--active {
  background: #ffd60a;
  border-color: #ffd60a;
  color: #0b1320;
  box-shadow: 0 2px 8px rgba(255, 214, 10, 0.3);
}

/* Lang badges */
[data-theme='dark'] .cpp__lang-badge {
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.5);
  border-color: rgba(255, 255, 255, 0.1);
}
[data-theme='dark'] .cpp__lang-badge--active {
  background: #ffd60a;
  color: #0b1320;
  border-color: #ffd60a;
}

/* Add button */
[data-theme='dark'] .cpp__add-btn {
  border-color: #ffd60a;
  color: #ffd60a;
  background: rgba(255, 214, 10, 0.08);
}
[data-theme='dark'] .cpp__add-btn:hover {
  background: rgba(255, 214, 10, 0.16);
}

/* Category chips (section top) */
[data-theme='dark'] .cpp__cat-chip {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.6);
}
[data-theme='dark'] .cpp__cat-chip:hover {
  border-color: #ffd60a;
  color: #ffd60a;
  background: rgba(255, 214, 10, 0.1);
}
[data-theme='dark'] .cpp__cat-chip--active {
  border-color: #ffd60a;
  background: rgba(255, 214, 10, 0.14);
  color: #ffd60a;
}

/* Category form */
[data-theme='dark'] .cpp__cat-form {
  border-color: #ffd60a;
}
[data-theme='dark'] .cpp__cat-form-title {
  color: #ffd60a;
}

/* Category list */
[data-theme='dark'] .cpp__cat-row {
  background: rgba(255, 255, 255, 0.03);
  border-color: rgba(255, 255, 255, 0.1);
}
[data-theme='dark'] .cpp__cat-row--selected {
  border-color: #ffd60a;
  background: rgba(255, 214, 10, 0.08);
}
[data-theme='dark'] .cpp__cat-check {
  border-color: rgba(255, 255, 255, 0.15);
  background: rgba(255, 255, 255, 0.05);
}
[data-theme='dark'] .cpp__cat-check--active {
  background: #ffd60a;
  border-color: #ffd60a;
  color: #0b1320;
}

/* Icon buttons */
[data-theme='dark'] .cpp__icon-btn {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.6);
}
[data-theme='dark'] .cpp__icon-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
}
[data-theme='dark'] .cpp__icon-btn--danger:hover {
  background: rgba(239, 68, 68, 0.12);
  color: #ef4444;
  border-color: rgba(239, 68, 68, 0.35);
}

/* Group number badge */
[data-theme='dark'] .cpp__group-num {
  background: #ffd60a;
  color: #0b1320;
}

/* Toggle */
[data-theme='dark'] .cpp__toggle-track {
  background: rgba(255, 255, 255, 0.15);
}
[data-theme='dark'] .cpp__toggle-input:checked + .cpp__toggle-track {
  background: #ffd60a;
}
[data-theme='dark'] .cpp__toggle-thumb {
  background: #fff;
}
[data-theme='dark']
  .cpp__toggle-input:checked
  + .cpp__toggle-track
  .cpp__toggle-thumb {
  background: #0b1320;
}

/* Buttons */
[data-theme='dark'] .cpp__btn--primary {
  background: #ffd60a;
  color: #0b1320;
  box-shadow: 0 2px 8px rgba(255, 214, 10, 0.3);
}
[data-theme='dark'] .cpp__btn--ghost {
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.6);
  border-color: rgba(255, 255, 255, 0.1);
}
[data-theme='dark'] .cpp__btn--ghost:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
}
[data-theme='dark'] .cpp__btn--outline {
  background: rgba(255, 214, 10, 0.06);
  color: #ffd60a;
  border-color: #ffd60a;
}
[data-theme='dark'] .cpp__btn--outline:hover {
  background: rgba(255, 214, 10, 0.14);
}

/* Image upload */
[data-theme='dark'] .cpp__img-preview-wrap {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.1);
}

/* Error banner */
[data-theme='dark'] .cpp__error-banner {
  background: rgba(239, 68, 68, 0.1);
  border-color: rgba(239, 68, 68, 0.3);
  color: #ff6b6b;
}

/* Modal icon */
[data-theme='dark'] .cpp__modal-icon {
  background: rgba(239, 68, 68, 0.12);
  border-color: rgba(239, 68, 68, 0.3);
}
</style>
